// @flow
import { useEffect, useState, useCallback } from 'react'
import { v4 as uuid } from 'uuid'

import {
	getMainObjective,
	useIsJunior,
	getMap,
	getPlusStationActivity,
	getJrStationActivity,
	useJuniorStateSelector,
	useJuniorPlusStateSelector,
	useMissionStateSelector,
} from '../../../context'
import { NO_OBJECTIVE } from '@mission.io/mission-toolkit/constants'
import { usePrevious } from '../../../utility'
import { type NotificationType, type Notification } from './constants'

export default function usePlayerNotifications(): Notification[] {
	const [notificationsArray, setNotificationsArray] = useState<Notification[]>(
		[]
	)

	const addNotification = useCallback(
		(type: NotificationType, value?: ?number, timerId?: ?string) => {
			const newId = uuid()
			const onEnd = (): void =>
				setNotificationsArray(array =>
					array.filter(notification => notification.id !== newId)
				)

			const newNotificationObject: Notification = { type, id: newId, onEnd }
			if (value) {
				newNotificationObject.value = value
			}
			setNotificationsArray(array => [...array, newNotificationObject])
			return
		},
		[]
	)

	useObjectiveWatcher(addNotification)
	useDefenseWatcher(addNotification)
	useTransporterWatcher(addNotification)
	useThrustersWatcher(addNotification)
	usePowerWatcher(addNotification)
	useCommunicationWatcher(addNotification)
	useTractorBeamWatcher(addNotification)
	useMapWatcher(addNotification)

	return notificationsArray
}

function useObjectiveWatcher(
	addNotification: (
		type: NotificationType,
		value?: ?number,
		timerId?: ?string
	) => void
) {
	const currentObjective = useMissionStateSelector(getMainObjective)
	const objectiveInfo = currentObjective.objectiveText
	const previousObjective = usePrevious(objectiveInfo)
	useEffect(() => {
		if (
			objectiveInfo !== previousObjective &&
			previousObjective != null &&
			objectiveInfo !== NO_OBJECTIVE
		) {
			addNotification('OBJECTIVE')
		}
	}, [objectiveInfo, addNotification, previousObjective])
}

function useDefenseWatcher(
	addNotification: (
		type: NotificationType,
		value?: ?number,
		timerId?: ?string
	) => void
) {
	const isJunior = useIsJunior()
	const juniorActive = useJuniorStateSelector(
		state => getJrStationActivity(state)?.defense
	)
	const juniorPlusActive = useJuniorPlusStateSelector(
		state => getPlusStationActivity(state)?.defensePlus
	)
	const isDefenseActive = isJunior ? juniorActive : juniorPlusActive
	const wasDefenseOn = usePrevious(isDefenseActive)
	useEffect(() => {
		if (isDefenseActive && isDefenseActive !== wasDefenseOn) {
			addNotification('DEFENSE')
		}
	}, [isDefenseActive, addNotification, wasDefenseOn])
}

function useTransporterWatcher(
	addNotification: (
		type: NotificationType,
		value?: ?number,
		timerId?: ?string
	) => void
) {
	const isJunior = useIsJunior()
	const juniorActive = useJuniorStateSelector(
		state => getJrStationActivity(state)?.transporter
	)
	const juniorPlusActive = useJuniorPlusStateSelector(
		state => getPlusStationActivity(state)?.transporterPlus
	)
	const isTransporterActive = isJunior ? juniorActive : juniorPlusActive
	const wasTransporterOn = usePrevious(isTransporterActive)
	useEffect(() => {
		if (isTransporterActive && isTransporterActive !== wasTransporterOn) {
			addNotification('TRANSPORTER')
		}
	}, [isTransporterActive, addNotification, wasTransporterOn])
}

function useThrustersWatcher(
	addNotification: (
		type: NotificationType,
		value?: ?number,
		timerId?: ?string
	) => void
) {
	const isJunior = useIsJunior()
	const juniorActive = useJuniorStateSelector(
		state => getJrStationActivity(state)?.thrusters
	)
	const juniorPlusActive = useJuniorPlusStateSelector(
		state => getPlusStationActivity(state)?.thrustersPlus
	)
	const isThrustersActive = isJunior ? juniorActive : juniorPlusActive
	const wasThrustersOn = usePrevious(isThrustersActive)

	useEffect(() => {
		if (isThrustersActive && isThrustersActive !== wasThrustersOn) {
			addNotification('THRUSTERS')
		}
	}, [isThrustersActive, addNotification, wasThrustersOn])
}

function usePowerWatcher(
	addNotification: (
		type: NotificationType,
		value?: ?number,
		timerId?: ?string
	) => void
) {
	const isJunior = useIsJunior()
	const juniorActive = useJuniorStateSelector(
		state => getJrStationActivity(state)?.power
	)
	const juniorPlusActive = useJuniorPlusStateSelector(
		state => getPlusStationActivity(state)?.powerPlus
	)
	const isPowerActive = isJunior ? juniorActive : juniorPlusActive
	const wasPowerOn = usePrevious(isPowerActive)

	useEffect(() => {
		if (isPowerActive && isPowerActive !== wasPowerOn) {
			addNotification('POWER')
		}
	}, [isPowerActive, addNotification, wasPowerOn])
}

function useCommunicationWatcher(
	addNotification: (
		type: NotificationType,
		value?: ?number,
		timerId?: ?string
	) => void
) {
	const isJunior = useIsJunior()
	const juniorActive = useJuniorStateSelector(
		state => getJrStationActivity(state)?.communication
	)
	const juniorPlusActive = useJuniorPlusStateSelector(
		state => getPlusStationActivity(state)?.communicationPlus
	)
	const isCommunicationsActive = isJunior ? juniorActive : juniorPlusActive
	const wasCommunicationsOn = usePrevious(isCommunicationsActive)

	useEffect(() => {
		if (
			isCommunicationsActive &&
			isCommunicationsActive !== wasCommunicationsOn
		) {
			addNotification('COMMUNICATION')
		}
	}, [isCommunicationsActive, addNotification, wasCommunicationsOn])
}

function useTractorBeamWatcher(
	addNotification: (
		type: NotificationType,
		value?: ?number,
		timerId?: ?string
	) => void
) {
	const isJunior = useIsJunior()
	const juniorActive = useJuniorStateSelector(
		state => getJrStationActivity(state)?.tractorBeam
	)
	const juniorPlusActive = useJuniorPlusStateSelector(
		state => getPlusStationActivity(state)?.tractorBeamPlus
	)
	const isTractorBeamActive = isJunior ? juniorActive : juniorPlusActive
	const wasTractorBeamOn = usePrevious(isTractorBeamActive)

	useEffect(() => {
		if (isTractorBeamActive && isTractorBeamActive !== wasTractorBeamOn) {
			addNotification('TRACTOR_BEAM')
		}
	}, [isTractorBeamActive, addNotification, wasTractorBeamOn])
}

function useMapWatcher(
	addNotification: (
		type: NotificationType,
		value?: ?number,
		timerId?: ?string
	) => void
) {
	const map = useJuniorPlusStateSelector(getMap)
	const mapId = map?._id
	const previousMapId = usePrevious(mapId)

	useEffect(() => {
		if (mapId && mapId !== previousMapId) {
			addNotification('MAP')
		}
	}, [mapId, addNotification, previousMapId])
}
